import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBooking } from "../../../../context/BookingContext";
import styles from "./GripBookingDetails.module.css";
import AddressForm from "../../AddressForm/AddressForm";

function joinAddress(...parts) {
  return parts
    .map((p) => (p ?? "").toString().trim())
    .filter((p) => p && p.toLowerCase() !== "null")
    .join(", ");
}
function ServiceSelect({ value, onChange, mode, disabled }) {
  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef(null);
  const listRef = React.useRef(null);

  const commonInStore = React.useMemo(
    () => [
      {
        value: "in-store",
        label: <>In-Store (No Charge)</>,
        plainTextLabel: "In-Store (No Charge)",
      },
    ],
    []
  );

  const regularOptions = React.useMemo(
    () => [
      ...commonInStore,
      {
        value: "pickup",
        label: (
          <>
            Pickup Only (+ <span className="dirham-symbol">&#xea;</span> 30)
          </>
        ),
        plainTextLabel: "Pickup Only (+ 30)",
      },
      {
        value: "delivery",
        label: (
          <>
            Delivery Only (+ <span className="dirham-symbol">&#xea;</span> 30)
          </>
        ),
        plainTextLabel: "Delivery Only (+ 30)",
      },
      {
        value: "pickup/delivery",
        label: (
          <>
            Both Pickup &amp; Delivery (+{" "}
            <span className="dirham-symbol">&#xea;</span> 60)
          </>
        ),
        plainTextLabel: "Both Pickup & Delivery (+ 60)",
      },
    ],
    [commonInStore]
  );

  const expressOptions = React.useMemo(
    () => [
      ...commonInStore,
      {
        value: "express-pickup",
        label: (
          <>
            Express Pickup (+ <span className="dirham-symbol">&#xea;</span> 50)
          </>
        ),
        plainTextLabel: "Express Pickup (+ 50)",
      },
      {
        value: "express-delivery",
        label: (
          <>
            Express Delivery (+ <span className="dirham-symbol">&#xea;</span>{" "}
            50)
          </>
        ),
        plainTextLabel: "Express Delivery (+ 50)",
      },
      {
        value: "express-pickup/delivery",
        label: (
          <>
            Express Pickup &amp; Delivery (+{" "}
            <span className="dirham-symbol">&#xea;</span> 100)
          </>
        ),
        plainTextLabel: "Express Pickup & Delivery (+ 100)",
      },
    ],
    [commonInStore]
  );

  const options = React.useMemo(
    () => (mode === "Express" ? expressOptions : regularOptions),
    [mode, expressOptions, regularOptions]
  );

  const current = options.find((o) => o.value === value) || options[0];

  React.useEffect(() => {
    if (!options.find((o) => o.value === value)) {
      onChange("in-store");
    }
  }, [options, value, onChange]);

  const [activeIndex, setActiveIndex] = React.useState(
    Math.max(
      0,
      options.findIndex((o) => o.value === value)
    )
  );

  React.useEffect(() => {
    setActiveIndex(
      Math.max(
        0,
        options.findIndex((o) => o.value === value)
      )
    );
  }, [options, value]);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!buttonRef.current || !listRef.current) return;
      if (
        !buttonRef.current.contains(e.target) &&
        !listRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    setActiveIndex(
      Math.max(
        0,
        options.findIndex((o) => o.value === value)
      )
    );
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + options.length) % options.length);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const opt = options[activeIndex];
      if (opt) {
        onChange(opt.value);
        setOpen(false);
      }
    }
  };

  return (
    <div className={styles.customSelect}>
      <button
        type="button"
        ref={buttonRef}
        className={styles.selectButton}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled ? "true" : "false"}
        disabled={!!disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
      >
        <span className={styles.selectButtonLabel}>{current.label}</span>
        <span className={styles.selectCaret}>▾</span>
      </button>

      {open && !disabled && (
        <ul
          className={styles.options}
          role="listbox"
          aria-activedescendant={`opt-${activeIndex}`}
          tabIndex={-1}
          ref={listRef}
        >
          {options.map((opt, idx) => (
            <li
              key={opt.value}
              id={`opt-${idx}`}
              role="option"
              aria-selected={opt.value === value}
              className={`${styles.option} ${
                opt.value === value ? styles.optionSelected : ""
              } ${idx === activeIndex ? styles.optionActive : ""}`}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
const BookingDetails = () => {
  const {
    setPickupService,
    addressMap,
    getAddressForCustomer,
    setAddressForCustomer,
  } = useBooking();

  const [validationError, setValidationError] = useState("");
  const location = useLocation();
  const {
    customerId,
    customerName,
    freeServices,
    usedServices,
    invoiceNo,
    freeServicesTotal,
    racketName: prefilledRacketName,
    isFreeService,
    rp_id,
    claim_id,
    lockServiceSelection = false,

    // NEW: allow either string or grip flows
    serviceKind = "string",
    string_type,
    grip_type,

    bookingDate = "",
    pickupService: carriedPickupService = null,
    instoreLocation: carriedInstoreLocation = "",
  } = location.state || {};

  const navigate = useNavigate();

  const effectiveKind =
    serviceKind || (typeof grip_type !== "undefined" ? "grip" : "string");
  const modeType =
    effectiveKind === "grip"
      ? grip_type === "Express"
        ? "Express"
        : "Regular"
      : string_type === "Express"
      ? "Express"
      : "Regular";

  const serviceLabel =
    effectiveKind === "grip" ? "Grip Service" : "Stringing Service";

  const [racketName] = useState(prefilledRacketName || "");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [pickupOption, setPickupOption] = useState(
    carriedPickupService || localStorage.getItem("pickupService") || "in-store"
  );
  const [instoreLocation, setInstoreLocation] = useState(
    carriedInstoreLocation || ""
  );

  const effectiveAddress =
    (getAddressForCustomer && getAddressForCustomer(customerId)) ||
    addressMap?.[customerId] ||
    "";

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/login", {
        state: { error: "Session expired. Please log in again." },
      });
    }
  }, [navigate]);

  const handleNextClick = () => {
    if (!racketName.trim()) {
      setValidationError("Please enter a racket name.");
      return;
    }

    if (!lockServiceSelection) {
      if (pickupOption === "in-store" && !instoreLocation) {
        setValidationError(
          "Please select a store location for in-store booking."
        );
        return;
      }
      if (pickupOption !== "in-store" && !effectiveAddress.trim()) {
        setValidationError("Please enter an address for the selected service.");
        return;
      }
    }

    setPickupService(pickupOption);
    setValidationError("");

    const nextState = {
      customerId,
      invoiceNo,
      customerName,
      freeServices,
      freeServicesTotal,
      usedServices,
      isFree: isFreeService,
      racketName,
      instoreLocation,
      rp_id,
      claim_id,
      bookingDate,
      serviceKind,
    };

    if (serviceKind === "grip") {
      nextState.gripType = modeType;
    } else {
      nextState.stringType = modeType;
    }

    navigate("/dashboard/grip-booking-final", { state: nextState });
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Create Booking</h2>

      <div className={styles.infoContainer}>
        <div className={styles.infoBlock}>
          <label className={styles.label}>Customer Name:</label>
          <span className={styles.disabledText}>{customerName}</span>
        </div>
        <div className={styles.infoBlock}>
          <label className={styles.label}>Service:</label>
          <span className={styles.disabledText}>{serviceLabel}</span>
        </div>
        <div className={styles.infoBlock}>
          <label className={styles.label}>Booking Date:</label>
          <input
            type="date"
            className={styles.dateInput}
            value={bookingDate}
            disabled
          />
        </div>
      </div>

      <div className={styles.infoContainer}>
        <div className={styles.infoBlock}>
          <label className={styles.label}>Choose Service:</label>
          <ServiceSelect
            value={pickupOption}
            onChange={setPickupOption}
            mode={modeType}
            disabled={lockServiceSelection}
          />

          {pickupOption === "in-store" && (
            <div className={styles.infoBlock}>
              <label className={styles.label}>Choose Store:</label>
              <select
                value={instoreLocation}
                onChange={(e) => setInstoreLocation(e.target.value)}
                className={styles.select}
                disabled={lockServiceSelection}
              >
                <option value="">-- Select a Store --</option>
                <option value="Jumeirah Store">Jumeirah Store</option>
                <option value="Marina Store">Marina Store</option>
                <option value="Dubai Hills Store">Dubai Hills Store</option>
              </select>
            </div>
          )}

          {pickupOption !== "in-store" && !lockServiceSelection && (
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className={styles.addAddressButton}
            >
              {effectiveAddress ? "Change Address" : "Add Address"}
            </button>
          )}
        </div>

        <div className={styles.tooltipContainer}>
          <span
            className={styles.tooltipLink}
            onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          >
            * Additional Charges applicable
          </span>
          {isTooltipOpen && (
            <div className={styles.tooltip}>
              <p>
                Pickup Only: +<span className="dirham-symbol">&#xea;</span> 30
              </p>
              <p>
                Delivery Only: +<span className="dirham-symbol">&#xea;</span> 30
              </p>
              <p>
                Both Pickup &amp; Delivery: +
                <span className="dirham-symbol">&#xea;</span> 60
              </p>
              <hr />
              <p>
                Express Pickup: +<span className="dirham-symbol">&#xea;</span>{" "}
                50
              </p>
              <p>
                Express Delivery: +<span className="dirham-symbol">&#xea;</span>{" "}
                50
              </p>
              <p>
                Express Pickup &amp; Delivery: +
                <span className="dirham-symbol">&#xea;</span> 100
              </p>
              <button
                className={styles.closeTooltip}
                onClick={() => setIsTooltipOpen(false)}
              >
                ✖
              </button>
            </div>
          )}
        </div>
      </div>

      {effectiveAddress && pickupOption !== "in-store" && (
        <div className={styles.infoBlock}>
          <label className={styles.label}>Selected Address:</label>
          <span className={styles.disabledText}>{effectiveAddress}</span>
        </div>
      )}

      <div className={styles.formGroup}>
        <label className={styles.label}>Racket Name:</label>
        <input
          className={styles.inputField}
          value={racketName}
          onChange={() => {}}
          placeholder="Enter racket name"
          disabled
          readOnly
          aria-readonly="true"
          title="Racket name is set in the previous step"
        />
      </div>

      {validationError && (
        <p className={styles.validationError}>{validationError}</p>
      )}

      <div className={styles.buttonGroup}>
        <button className={styles.cancelButton} onClick={() => navigate(-1)}>
          Cancel
        </button>
        <button className={styles.nextButton} onClick={handleNextClick}>
          Next
        </button>
      </div>

      {isModalOpen && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Select or Add Address</h3>
            <AddressForm
              onSubmit={(addr) => {
                const formattedAddress = joinAddress(
                  addr.address_line,
                  addr.area,
                  addr.pincode,
                  addr.state,
                  addr.country
                );
                setAddressForCustomer(customerId, formattedAddress);
                setIsModalOpen(false);
              }}
              customerId={customerId}
            />
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className={styles.closeModalButton}
            >
              ✖
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetails;
